import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { baseURL, getHeaders, VERSION } from '../constant/constant';
import { ResponseBase } from '../constant/response';
import { RecruitmentCandidate, RecruitmentChangeHistory, RecruitmentStatus, ResumeFile } from '../components/recruitment/recruitment.model';
import { RecruitmentCandidateListApi, RecruitmentDocumentApi, RecruitmentCandidateApi, RecruitmentStats} from '../components/recruitment/recruitment.constants';

//TODO : Need to imporve recruitment.service.ts 
//      --> make a filter DTO , use responsebaseDTO ,handle map/
@Injectable({
  providedIn: 'root'
})
export class RecruitmentService {
  private readonly apiUrl = `${baseURL}recruitment/${VERSION}`;

  constructor(private http: HttpClient) {}

  uploadResumes(files: File[]): Observable<RecruitmentCandidate[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return this.http.post<ResponseBase<RecruitmentCandidateApi[]>>(`${this.apiUrl}/upload-resumes`, formData).pipe(
      map((response) => response.responseObject.map((candidate) => this.toCandidate(candidate)))
    );
  }

  getCandidates(filters: {
    search?: string;
    status?: RecruitmentStatus;
    reviewed?: boolean;
    interviewTaken?: boolean;
    interviewerName?: string;
    page: number;
    size?: number;
  }): Observable<{ candidates: RecruitmentCandidate[]; totalRecords: number }> {

    let params = new HttpParams()
      .set('page', filters.page ?? 0)
      .set('size', filters.size ?? 10);

    if (filters.search) {
      params = params.set('search', filters.search);
    }
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.reviewed !== undefined) {
      params = params.set('reviewed', filters.reviewed);
    }
    if (filters.interviewTaken !== undefined) {
      params = params.set('interviewTaken', filters.interviewTaken);
    }
    if (filters.interviewerName) {
      params = params.set('interviewerName', filters.interviewerName);
    }

    return this.http.get<ResponseBase<RecruitmentCandidateListApi>>(`${this.apiUrl}/candidates`, {
      ...getHeaders(),
      params
    }).pipe(
      map((response) => ({
        candidates: response.responseObject.responseData.map((candidate) => this.toCandidate(candidate)),
        totalRecords: response.responseObject.totalRecords
      }))
    );
  }

  getStats(): Observable<RecruitmentStats> {
    return this.http.get<ResponseBase<RecruitmentStats>>(`${this.apiUrl}/stats`, getHeaders()).pipe(
      map((response) => response.responseObject)
    );
  }

  getCandidate(candidateId: number): Observable<RecruitmentCandidate> {
    return this.http.get<ResponseBase<RecruitmentCandidateApi>>(`${this.apiUrl}/candidates/${candidateId}`, getHeaders()).pipe(
      map((response) => this.toCandidate(response.responseObject))
    );
  }

  getChangeHistory(): Observable<RecruitmentChangeHistory[]> {
    return this.http.get<ResponseBase<RecruitmentChangeHistory[]>>(`${this.apiUrl}/change-history`, getHeaders()).pipe(
      map((response) => response.responseObject.map((row) => ({
        ...row,
        createdDate: this.formatDate(row.createdDate)
      })))
    );
  }

  getCandidateHistory(candidateId: number): Observable<RecruitmentChangeHistory[]> {
    return this.http.get<ResponseBase<RecruitmentChangeHistory[]>>(
      `${this.apiUrl}/candidates/${candidateId}/status-history`,
      getHeaders()
    ).pipe(
      map((response) => response.responseObject.map((row) => ({
        ...row,
        createdDate: this.formatDate(row.createdDate)
      })))
    );
  }

  exportChangeHistory(search?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get(`${this.apiUrl}/export-change-history`, {
      ...getHeaders(),
      params,
      responseType: 'blob'
    });
  }

  updateCandidate(candidate: RecruitmentCandidate): Observable<RecruitmentCandidate> {
    return this.http.put<ResponseBase<RecruitmentCandidateApi>>(
      `${this.apiUrl}/candidates/${candidate.id}`,
      {
        fullName: candidate.name,
        email: candidate.email,
        contactNumber: candidate.contactNumber,
        totalExperience: candidate.experience,
        status: candidate.status,
        reviewed: candidate.reviewed,
        reviewedBy: candidate.reviewedBy,
        sourceOfResume: candidate.sourceOfResume,
        departmentId: candidate.departmentId,
        departmentName: candidate.departmentName,
        cvUploadedOn: candidate.cvUploadedOn,
        reviewedOn: candidate.reviewedOn,
        interviewerName: candidate.interviewerName,
        interviewRound: candidate.interviewRound,
        technicalSkills: candidate.technicalSkills,
        overallPercentage: candidate.overallPercentage,
        recommendationStatus: candidate.recommendationStatus,
        interviewTakenTime: candidate.interviewTaken,
        currentCompany: candidate.currentCompany,
        skills: candidate.skills,
        techStack: candidate.techStack,
        education: candidate.education,
        remarks: candidate.remarks
      },
      getHeaders()
    ).pipe(map((response) => this.toCandidate(response.responseObject)));
  }

  uploadDocuments(candidateId: number, files: File[]): Observable<ResumeFile[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return this.http.post<ResponseBase<RecruitmentDocumentApi[]>>(
      `${this.apiUrl}/candidates/${candidateId}/documents`,
      formData
    ).pipe(
      map((response) => response.responseObject.map((document) => this.toResumeFile(document)))
    );
  }
//  thi is use for document delete 
  deleteDocument(candidateId: number, documentId: number): Observable<ResponseBase<null>> {
    return this.http.delete<ResponseBase<null>>(
      `${this.apiUrl}/candidates/${candidateId}/documents/${documentId}`,
      getHeaders()
    );
  }

  downloadDocument(candidateId: number, documentId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/candidates/${candidateId}/documents/${documentId}/download`, {
      ...getHeaders(),
      responseType: 'blob'
    });
  }

  private toCandidate(candidate: RecruitmentCandidateApi): RecruitmentCandidate {
    return {
      id: candidate.id,
      name: candidate.fullName,
      email: candidate.email ?? '',
      contactNumber: candidate.contactNumber ?? '',
      experience: candidate.totalExperience ?? '',
      status: candidate.status,
      reviewed: candidate.reviewed,
      sourceOfResume: candidate.sourceOfResume ?? '',
      cvUploadedOn: this.getCvUploadedDate(candidate),
      reviewedOn: this.formatDateTime(candidate.reviewedOn || candidate.interviewScheduledOn || candidate.scheduledOn),
      interviewTaken: this.formatDate(candidate.interviewTakenTime) || '-',
      reviewedBy: candidate.reviewedBy ?? '',
      interviewerName: candidate.interviewerName ?? '',
      departmentId: candidate.departmentId ?? candidate.departmentID ?? candidate.department_id ?? candidate.deptId ?? '',
      departmentName: candidate.departmentName || candidate.department || '',
      interviewRound: candidate.interviewRound ?? '',
      technicalSkills: candidate.technicalSkills ?? '',
      overallPercentage: candidate.overallPercentage ?? null,
      recommendationStatus: (candidate.recommendationStatus ?? '') as any,
      currentCompany: candidate.currentCompany ?? '',
      skills: candidate.skills ?? '',
      techStack: candidate.techStack ?? '',
      education: candidate.education ?? '',
      remarks: candidate.remarks ?? '',
      resumeFile: candidate.sourceResumeFileName ?? '',
      msaDocuments: (candidate.documents ?? []).map((document) => this.toResumeFile(document))
    };
  }

  private toResumeFile(document: RecruitmentDocumentApi): ResumeFile {
    return {
      id: document.id,
      name: document.fileName,
      size: '',
      uploadedOn: '',
      status: 'Uploaded'
    };
  }

  private getCvUploadedDate(candidate: RecruitmentCandidateApi): string {
    return this.formatDate(
      candidate.createdDate ||
      candidate.createdAt ||
      candidate.createDate ||
      candidate.created_date ||
      candidate.created_on ||
      candidate.cvUploadedOn ||
      candidate.resumeUploadedOn ||
      candidate.uploadedOn ||
      candidate.parsedOn
    );
  }

  private formatFileSize(bytes: number): string {
    if (!bytes) {
      return '';
    }
    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private formatDate(value?: string): string {
    if (!value) {
      return '';
    }

    const dateOnly = value.trim().split(' ')[0];
    const dateParts = dateOnly.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dateParts) {
      return `${dateParts[1]}-${dateParts[2]}-${dateParts[3]}`;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    const day = `${parsed.getDate()}`.padStart(2, '0');
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    return `${day}-${month}-${parsed.getFullYear()}`;
  }

  private formatDateTime(value?: string): string {
    if (!value) {
      return '';
    }

    const dateTimeParts = value.trim().match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
    if (dateTimeParts) {
      return dateTimeParts[4]
        ? `${dateTimeParts[1]}-${dateTimeParts[2]}-${dateTimeParts[3]} ${dateTimeParts[4]}:${dateTimeParts[5]}`
        : `${dateTimeParts[1]}-${dateTimeParts[2]}-${dateTimeParts[3]}`;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    const day = `${parsed.getDate()}`.padStart(2, '0');
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const hours = `${parsed.getHours()}`.padStart(2, '0');
    const minutes = `${parsed.getMinutes()}`.padStart(2, '0');
    return `${day}-${month}-${parsed.getFullYear()} ${hours}:${minutes}`;
  }
}
export { RecruitmentStats };
